import { useState } from 'react';
import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { cancelAnalysis, useAnalysisAction, type AnalysisStaff } from '@/api/analyses';
import { errorMessage } from '@/api/errors';

/**
 * Cancel an analysis, with an internal reason. `defective` words it as the QA verdict on an
 * analysis in review (the project page's "mark defective"); the transition is the same.
 */
export function CancelAnalysisModal({ analysis, opened, onClose, defective = false }: { analysis: AnalysisStaff; opened: boolean; onClose: () => void; defective?: boolean }) {
  const { t } = useTranslation('analyses');
  const { t: tc } = useTranslation();
  const cancel = useAnalysisAction(cancelAnalysis);
  const [reason, setReason] = useState('');
  const go = async () => {
    try {
      await cancel.mutateAsync({ id: analysis.public_id, args: [reason] });
      if (defective) notifications.show({ color: 'forest', message: t('defective.done') });
      onClose();
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };
  const confirm = defective ? t('defective.confirm') : t('actions.cancel');
  return (
    <Modal opened={opened} onClose={onClose} title={defective ? t('defective.title') : t('actions.cancel')}>
      <Stack gap="md">
        <Text fz={14}>{defective ? t('defective.hint') : t('actions.cancelHint')}</Text>
        <Textarea label={defective ? t('defective.reason') : t('actions.reason')} value={reason} onChange={(e) => setReason(e.currentTarget.value)} rows={3} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {tc('common.cancel')}
          </Button>
          <Button color="red" loading={cancel.isPending} onClick={() => void go()}>
            {confirm}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
